/**
 * /cheap-sim-cards — Primary SEO landing page
 * Target keywords: cheap sim cards, cheap sim card UK, affordable SIM, cheap SIM deals
 * Beats: Lebara, Giffgaff, SMARTY — all miss dedicated cheap-sim landing pages with full schema
 */
import { Link } from 'react-router-dom'
import { CheckCircle, ArrowRight, Smartphone, Star, Shield, Zap, Phone, Globe } from 'lucide-react'
import SEOHead from '@/components/SEOHead'
import SiluxPromise from '@/components/SiluxPromise'
import { useQuery } from '@tanstack/react-query'
import api from '@/services/api'

const FAQS = [
  {
    question: 'What is the cheapest SIM card in the UK?',
    answer: 'Silux Mobile offers some of the most affordable SIM cards in the UK, starting from as little as £5/month on Three, Vodafone, EE and O2. Our data-only SIM cards, calls & text SIMs and multi-SIM bundles are competitively priced with no hidden fees.',
  },
  {
    question: 'Are there any contracts with cheap SIM cards?',
    answer: 'You choose. Our SIM cards are available on 30-day rolling plans with no minimum term, or on 3, 6, 12 or 24-month contracts at a lower monthly price.',
  },
  {
    question: 'What network does Silux Mobile use?',
    answer: 'Silux Mobile SIMs are available on all the UK\'s major networks — Three, Vodafone, EE and O2 — so you can choose the network with the best coverage where you live. Check your postcode on our coverage checker before you order.',
  },
  {
    question: 'Do cheap SIM cards have 5G?',
    answer: 'Yes — all Silux Mobile SIM cards include 5G at no extra cost where coverage is available. Our network covers over 99% of the UK population outdoors.',
  },
  {
    question: 'Can I keep my existing number?',
    answer: 'Yes, on a SIM contract — number transfers (PAC) are free. We process PAC transfers Monday to Thursday. Three to Three takes 2–3 working days; from another network (for example O2 to Three, Vodafone to O2 or Vodafone to Three) it takes 1–2 working days. Due to our agreements with the networks, we are unable to PAC transfer a number onto a Pay&Go SIM.',
  },
  {
    question: 'Will my price increase during my contract?',
    answer: 'No. Silux Mobile never raises your price mid-contract — there are no price increases during your contract and no annual or inflation-linked price rises. The price you sign up at is the price you pay.',
  },
  {
    question: 'Are plan and SIM upgrades free?',
    answer: 'Yes. Upgrades are free — move to a bigger data plan or a better SIM whenever you like at no extra cost, with no upgrade or admin fees.',
  },
  {
    question: 'Do you run a credit check?',
    answer: 'No. There are no credit checks on our SIM contracts, so you can join Silux Mobile regardless of your credit history. No credit check is required to order or activate your SIM.',
  },
  {
    question: 'Is Silux Mobile a good mobile provider?',
    answer: 'Yes. Silux Mobile is a UK SIM-only and mobile network provider with SIMs on Three, Vodafone, EE and O2, with cheap SIM deals, no price rises, free upgrades and no credit checks on SIM contracts.',
  },
]

const PRODUCTS = [
  {
    name: 'Data Only SIM',
    price: 'From £5/mo',
    features: ['No calls/texts included', 'Perfect for tablets & MiFi', '5G included', '30-day or contract'],
    to: '/sim-only/data-only',
    highlight: false,
  },
  {
    name: 'Calls, Text & Data SIM',
    price: 'From £8/mo',
    features: ['Unlimited UK calls', 'Unlimited UK texts', '5G included', '30-day or contract'],
    to: '/sim-only/calls-text-data',
    highlight: true,
    badge: 'Most Popular',
  },
  {
    name: 'Multi-SIM Deal',
    price: 'From £10/mo',
    features: ['Multiple SIMs, one bill', '3 or 4 networks', 'Great for families', 'Bulk discounts'],
    to: '/sim-only/multisim',
    highlight: false,
  },
  {
    name: 'Free SIM Card',
    price: 'FREE',
    features: ['No upfront cost', 'Delivered free', 'Choose plan on activation', 'No obligation'],
    to: '/sim-only',
    highlight: false,
    badge: 'Free',
  },
]

export default function CheapSimCards() {
  const { data: _productsData } = useQuery({
    queryKey: ['products-sim'],
    queryFn: async () => {
      const res = await api.get('/products', { params: { category: 'SIM Only' } })
      return res.data
    },
  })

  const breadcrumbs = [
    { name: 'Home', url: 'https://www.siluxmobile.co.uk/' },
    { name: 'Cheap SIM Cards', url: 'https://www.siluxmobile.co.uk/cheap-sim-cards' },
  ]

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Cheap SIM Cards UK — Silux Mobile',
    description: 'Affordable SIM cards from Silux Mobile on Three network — data SIMs, calls & text SIMs, multi-SIMs and free SIMs.',
    numberOfItems: PRODUCTS.length,
    itemListElement: PRODUCTS.map((p, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: p.name,
      description: p.features.join('. '),
    })),
  }

  return (
    <>
      <SEOHead
        title="Cheap SIM Cards UK | From £5/Month | Silux Mobile"
        description="Get the cheapest SIM cards in the UK from Silux Mobile. Data SIMs from £5/mo, calls & text SIMs, free SIM cards, 5G included. No contracts. Three network. Order online today. No price rises, free upgrades and no credit checks on SIM contracts."
        keywords={[
          'cheap sim cards', 'cheap sim card UK', 'affordable SIM UK', 'cheap sim deals',
          'cheap mobile SIM', 'cheap data sim', 'SIM only deals UK', 'no contract SIM card',
          'free SIM card UK', '5G SIM card UK', 'cheap SIM 2024', 'best cheap SIM UK',
          'SIM only provider', 'best SIM only provider UK', 'mobile network provider',
          'cheap mobile provider', 'data SIM provider', 'pay monthly SIM provider',
        ]}
        canonical="https://www.siluxmobile.co.uk/cheap-sim-cards"
        breadcrumbs={breadcrumbs}
        faqs={FAQS}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgSchema) }} />

      {/* Hero */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-primary-700 via-primary-600 to-cyan-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('/grid.svg')]" />
        <div className="max-w-5xl mx-auto relative text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 text-sm font-medium">
            <Smartphone className="w-4 h-4" />
            UK's Most Affordable SIM Cards — Three Network
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Cheap SIM Cards UK
            <span className="block text-2xl md:text-3xl font-normal text-white/80 mt-2">
              From just £5/month — 5G included, 30-day rolling or contract
            </span>
          </h1>
          <p className="text-lg md:text-xl text-white/80 max-w-2xl mx-auto mb-8">
            Silux Mobile is a leading SIM only provider and mobile network provider on the powerful Three network — the cheap mobile provider for the UK.
            Data SIMs, calls & text SIMs, multi-SIMs and free SIM cards — all with no hidden fees.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/sim-only"
              className="px-8 py-4 bg-white text-primary-700 rounded-2xl font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              View All SIM Plans
            </Link>
            <Link
              to="/sim-only"
              className="px-8 py-4 bg-white/20 border-2 border-white/40 text-white rounded-2xl font-bold hover:bg-white/30 transition-all"
            >
              Get Free SIM Card
            </Link>
          </div>

          {/* Social proof */}
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-white/70">
            <span className="flex items-center gap-2"><Star className="w-4 h-4 text-yellow-400 fill-yellow-400" /> 4.5/5 Google Rating</span>
            <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-green-400" /> No Credit Check Required</span>
            <span className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400" /> 30-Day Rolling or Contract</span>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="py-6 px-4 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          {[
            { icon: Zap, title: '5G Included', sub: 'Fastest network speeds' },
            { icon: Shield, title: 'Your Choice', sub: '30-day rolling or 3–24 month contracts' },
            { icon: Globe, title: 'Go Roam', sub: '71+ countries — extra cost, varies by package' },
            { icon: Phone, title: 'Keep Your Number', sub: 'Free PAC transfer on contracts' },
          ].map(item => (
            <div key={item.title} className="p-4">
              <item.icon className="w-8 h-8 text-primary-600 mx-auto mb-2" />
              <p className="font-bold text-gray-900 text-sm">{item.title}</p>
              <p className="text-xs text-gray-500">{item.sub}</p>
            </div>
          ))}
        </div>
      </section>

      <SiluxPromise variant="sim" />

      {/* Product Cards */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-3">
              Our Cheapest SIM Card Deals
            </h2>
            <p className="text-lg text-gray-600">
              SIMs available on Three, Vodafone, EE and O2 — pick the plan that suits you.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRODUCTS.map(product => (
              <div
                key={product.name}
                className={`relative rounded-2xl p-6 border-2 transition-all hover:shadow-xl hover:-translate-y-1 ${
                  product.highlight
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-200 bg-white'
                }`}
              >
                {product.badge && (
                  <span className={`absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold px-3 py-1 rounded-full ${
                    product.badge === 'Free' ? 'bg-green-500 text-white' : 'bg-primary-600 text-white'
                  }`}>
                    {product.badge}
                  </span>
                )}
                <h3 className="font-bold text-gray-900 text-lg mb-1">{product.name}</h3>
                <p className={`text-2xl font-black mb-4 ${product.highlight ? 'text-primary-600' : 'text-gray-900'}`}>
                  {product.price}
                </p>
                <ul className="space-y-2 mb-6">
                  {product.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {f}
                    </li>
                  ))}
                </ul>
                <Link
                  to={product.to}
                  className={`block text-center py-3 rounded-xl font-semibold text-sm transition-all ${
                    product.highlight
                      ? 'bg-primary-600 text-white hover:bg-primary-700'
                      : 'bg-gray-900 text-white hover:bg-gray-800'
                  }`}
                >
                  Get This SIM
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Silux vs Competitors */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            Why Silux Mobile Beats the Competition
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            We compared the biggest UK SIM providers — here's why Silux Mobile offers better value than Lebara, Giffgaff, SMARTY and iD Mobile.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-primary-600 text-white">
                  <th className="px-4 py-3 text-left rounded-tl-xl">Feature</th>
                  <th className="px-4 py-3 text-center font-bold">Silux Mobile</th>
                  <th className="px-4 py-3 text-center text-white/70">Lebara</th>
                  <th className="px-4 py-3 text-center text-white/70">Giffgaff</th>
                  <th className="px-4 py-3 text-center text-white/70 rounded-tr-xl">SMARTY</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['5G Included', '✅', '❌', '✅', '✅'],
                  ['30-Day Rolling Option', '✅', '✅', '✅', '✅'],
                  ['No Price Rises*', '✅', '✅', '✅', '✅'],
                  ['Free Upgrades', '✅', '—', '—', '—'],
                  ['No Credit Check', '✅', '✅', '✅', '✅'],
                  ['Free SIM Card', '✅', '✅', '✅', '✅'],
                  ['Multi-SIM Plans', '✅', '❌', '❌', '❌'],
                  ['Data-Only SIM', '✅', '❌', '❌', '✅'],
                  ['Go Roam (71+ countries)', '✅', '❌', '❌', '❌'],
                  ['Long-Life SIM', '✅', '❌', '❌', '❌'],
                  ['VoIP Services', '✅', '❌', '❌', '❌'],
                  ['UK-based support', '✅', '❌', '✅', '❌'],
                  ['Broadband Bundles', '✅', '❌', '❌', '❌'],
                ].map(([feature, ...values]) => (
                  <tr key={feature as string} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-800">{feature}</td>
                    <td className="px-4 py-3 text-center font-bold text-primary-600">{values[0]}</td>
                    <td className="px-4 py-3 text-center text-gray-500">{values[1]}</td>
                    <td className="px-4 py-3 text-center text-gray-500">{values[2]}</td>
                    <td className="px-4 py-3 text-center text-gray-500">{values[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 mt-4 text-center max-w-3xl mx-auto">
            *No price rises: Silux Mobile and SMARTY never raise your price; Lebara and giffgaff guarantee no price rises during your minimum term. Comparison based on publicly available plan information and is for guidance only.
          </p>
        </div>
      </section>

      {/* No price rises vs the big networks — topical 2026 angle */}
      <section className="py-16 px-4 bg-white border-t border-gray-100">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-4">
            No Price Rises. Free Upgrades. No Credit Checks.
          </h2>
          <p className="text-center text-gray-600 mb-10 max-w-2xl mx-auto">
            The big networks now add fixed yearly price rises to your bill mid-contract — Ofcom's
            2025 rules made them spell the increases out in pounds and pence, but they're still going up.
            Silux Mobile is different: the price you join on is the price you keep.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                title: 'No price increases',
                body: 'Your monthly price is locked for as long as you stay on your plan. No annual rises, no mid-contract price increases — unlike EE, O2, Three and Vodafone, who add fixed yearly increases (Vodafone, for example, is adding £3.50/month from April 2026).',
              },
              {
                title: 'Free upgrades',
                body: 'Move to a bigger data plan or a better SIM whenever you like, free of charge. No upgrade fees and no admin fees — and the same applies to our free broadband upgrades.',
              },
              {
                title: 'No credit checks',
                body: 'There are no credit checks on our SIM contracts, so you can join regardless of your credit history. Most big-network pay monthly plans run a hard credit check — we don\'t.',
              },
            ].map(card => (
              <div key={card.title} className="rounded-2xl border border-gray-200 bg-gray-50 p-6">
                <h3 className="font-bold text-gray-900 mb-2">{card.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* GEO Section */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
            Cheap SIM Cards Available Across the UK
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Order online and get your cheap SIM card delivered free by Royal Mail Tracked 48 (2–3 working days) anywhere in the UK — or choose faster delivery at checkout.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              'London', 'Manchester', 'Birmingham', 'Leeds',
              'Glasgow', 'Edinburgh', 'Bristol', 'Cardiff',
              'Sheffield', 'Liverpool', 'Newcastle', 'Nottingham',
              'Leicester', 'Coventry', 'Bradford', 'Belfast',
            ].map(city => (
              <div key={city} className="bg-white rounded-xl px-4 py-3 text-center text-sm font-medium text-gray-700 border border-gray-200">
                {city}
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-4">
            All orders dispatched within 1 working day via Royal Mail. Free delivery on all SIM cards.
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">
            Cheap SIM Card FAQs
          </h2>
          <div className="space-y-4">
            {FAQS.map(faq => (
              <details key={faq.question} className="group border border-gray-200 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer font-semibold text-gray-900 hover:bg-gray-50">
                  {faq.question}
                  <ArrowRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">
                  {faq.answer}
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Cross-links */}
      <section className="py-12 px-4 bg-primary-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">Related Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Data SIM Cards', desc: 'Pure data, no calls', to: '/data-sim-cards' },
              { label: 'Mobile Broadband', desc: '4G/5G home broadband', to: '/mobile-broadband' },
              { label: 'Free SIM Card', desc: 'Order today, free delivery', to: '/sim-only' },
              { label: 'Broadband Deals', desc: 'Cheap broadband UK', to: '/cheap-broadband' },
              { label: 'SIM Only Deals', desc: 'All SIM plans', to: '/sim-only' },
              { label: 'No Contract SIM', desc: '30-day rolling', to: '/no-contract-sim' },
              { label: 'Roaming SIMs', desc: 'Use abroad cheaply', to: '/roaming' },
              { label: 'Network Coverage', desc: 'Check your area', to: '/network-coverage' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="bg-white rounded-xl p-4 border border-gray-200 hover:border-primary-300 hover:shadow-sm transition-all"
              >
                <p className="font-semibold text-gray-900 text-sm">{link.label}</p>
                <p className="text-xs text-gray-500 mt-1">{link.desc}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
